//----------------CORE-----------------//
import React from "react";
import i18n from "i18next";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { resetSocket } from "Utilities/socket";
const camelcase = require("camelcase");
//----------------MUI-----------------//
import { makeStyles } from "@mui/styles";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Divider from "@mui/material/Divider";
import ListItemIcon from "@mui/material/ListItemIcon";
import Avatar from "@mui/material/Avatar";
//----------------MUI ICONS-----------------//
import LogoutIcon from "@mui/icons-material/Logout";
import LanguageIcon from "@mui/icons-material/Language";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import Person from "@mui/icons-material/Person";
import AppsIcon from "@mui/icons-material/Apps";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import CodeIcon from "@mui/icons-material/Code";
import WebIcon from "@mui/icons-material/Web";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
//----------------EXTERNAL COMPONENTS-----------------//
import { resetMetaData, PERMISSION_ONLY_MODULES } from "rtkSlices/metaDataSlice";
import { clearRedirect } from "Utilities/postLoginRedirect";
import { useLazyLogoutQuery } from "services/auth";
import Popover from "components/Popover";
import Search from "./Search";

export default function AdminNavbarLinks(props) {
  const metaDataValue = useSelector((state) => state.metaData);
  const useStyles = makeStyles({
    gridElm: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100%",
      width: "100px",
      margin: "5px",
      transition: "0.3s",
      borderRadius: "10px",
      "&:hover": {
        transform: "translate(0, -3px)",
        boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
      },
    },
    gridItem: {
      textAlign: "center",
      width: "110px",
      height: "70px",
      padding: "10px",
      borderRadius: "10px",
      "&:hover": {
        color: metaDataValue.branding?.primaryColor,
      },
    },
    gridRoot: {
      display: "flex",
      padding: "8px 10px 0px 10px",
    },
    title: {
      fontSize: "10px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  });
  const classes = useStyles(props);
  const dispatch = useDispatch();

  const [openNotification, setOpenNotification] = React.useState(null);
  const [openProfile, setOpenProfile] = React.useState(null);
  const [openLanguage, setOpenLanguage] = React.useState(null);

  const [logout] = useLazyLogoutQuery();

  function popperLanguage() {
    return (
      <MenuList role="menu">
        <MenuItem
          id="en"
          onClick={() => {
            changeLanguage("en");
          }}
          // className={classes.dropdownItem}
          style={{
            backgroundColor:
              window.localStorage.getItem("Language") == "en"
                ? metaDataValue.branding?.primaryColor
                : "",
            color:
              window.localStorage.getItem("Language") == "en" ? "white" : "",
          }}
        >
          English
        </MenuItem>
        {/* <Divider light /> */}
        {/* <MenuItem
          onClick={() => {
            changeLanguage("ar");
          }}
          // className={classes.dropdownItem}
          style={{
            backgroundColor:
              window.localStorage.getItem("Language") == "ar" ? "#3399ff" : "",
            color:
              window.localStorage.getItem("Language") == "ar" ? "white" : "",
          }}
        >
          <p style={{ textAlign: "right" }}>عربى</p>
        </MenuItem> */}
      </MenuList>
    );
  }

  function popperGrid() {
    // Resolve the current service from localStorage so the Alarms Management
    // tile can hand it to /alarms-dashboard as `selectedSolution`. Without
    // this, opening alarms from inside a Solution Layout would lose the
    // solution context and default to "All Solutions".
    const alarmsCurrentService = metaDataValue.services?.find(
      (s) => s.id === window.localStorage.getItem("currentService")
    );
    // Fleet Reports rides on EXISTING Analytics access (product decision: no
    // separate RBAC module for it). A user sees the tile only if their role already
    // grants the Analytics module AND the tenant has the fleetKpi capability. The
    // tenant capability (requireFleetKpi) is the real server boundary; this is the
    // FE visibility gate — deliberately no new rolesMS module/migration.
    const hasAnalytics = metaDataValue.apps?.some((a) => a.name === "Analytics");
    // `|| []` everywhere: metaData.apps has no initial-state default (it is
    // undefined until setRole lands), and chunk() below reduces over it — an
    // undefined gridApps would crash the nav menu for ANY tenant whose menu is
    // opened before the role fetch resolves.
    // Permission-only module groups (Solution Features, Solution Edit
    // Parts) carry Base Permissions, never tiles.
    let gridAppsBase = (metaDataValue.apps || []).filter(
      (a) => a && !PERMISSION_ONLY_MODULES.includes(a.name)
    );
    // A ROLE module (an entry carrying `tabs`) needs at least one navigable path in its
    // layout, else its tile leads to a dead end (a module whose tabs are all disabled still
    // reaches metaData.apps; see the layouts' fallback redirects). The built-in Solutions and
    // Support entries carry no tabs and no appPaths by design, so they are never filtered.
    // metaData.apps itself is left complete (the role editor's caller-holdings fallback).
    const navigable = (name) =>
      (metaDataValue.appPaths || []).some(
        (p) => p.layout === `/${camelcase(name).replace(" ", "")}/`
      );
    gridAppsBase = gridAppsBase.filter((a) => !a.tabs || navigable(a.name));
    // Synthetic Alarms tile: the alarms dashboard is navigable for holders of
    // the Solution Features > Alarms master (superadmin bypasses).
    const holdsAlarmsFeature =
      metaDataValue.unscopedAdmin ||
      ["ALL", "READ"].includes(
        metaDataValue.modulePermissions?.["Solution Features"]?.Alarms
      );
    if (holdsAlarmsFeature && !gridAppsBase.some((a) => a.name === "Alarms")) {
      gridAppsBase = [...gridAppsBase, { name: "Alarms" }];
    }
    const gridApps =
      metaDataValue.fleetKpi?.enabled === true &&
      hasAnalytics &&
      !gridAppsBase.some((a) => a.name === "Fleet Reports")
        ? [...gridAppsBase, { name: "Fleet Reports" }]
        : gridAppsBase;
    return (
      <MenuList role="menu">
        {chunk(gridApps, 3).map((chunk) => (
          <div className={classes.gridRoot}>
            {chunk.map((elm) => {
              const linkTo =
                elm.name === "Alarms" && alarmsCurrentService
                  ? {
                      pathname: paths[elm.name]?.path,
                      state: { selectedSolution: alarmsCurrentService },
                    }
                  : paths[elm.name]?.path;
              return (
                <span className={classes.gridElm}>
                  <Link
                    id={`module-${elm.name}`}
                    to={linkTo}
                    className={classes.gridItem}
                    style={{
                      color: window.location.pathname.includes(
                        camelcase(elm.name)
                      )
                        ? metaDataValue.branding?.primaryColor
                        : "#757575",
                    }}
                    key={`grid-menu${elm.name}`}
                  >
                    {generateIcon(paths[elm.name]?.icon, elm.name)}
                  </Link>
                </span>
              );
            })}
          </div>
        ))}
      </MenuList>
    );
  }

  function popperProfile() {
    return (
      <MenuList role="menu">
        <MenuItem>
          <Avatar />
          <span style={{ marginLeft: "20px", fontSize: "15px" }}>
            <p>{`${metaDataValue.userInfo?.firstName} ${metaDataValue.userInfo?.lastName}`}</p>
            <p style={{ fontSize: "12px", color: "#bdbdbd" }}>
              {metaDataValue.userInfo?.email}
            </p>
          </span>
        </MenuItem>
        <Divider />
        <MenuItem id="logout" onClick={logoutFn}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          {t("logout")}
        </MenuItem>
      </MenuList>
    );
  }

  const { t } = useTranslation();

  const handleClickNotification = (event) => {
    if (openNotification && openNotification.contains(event.target)) {
      setOpenNotification(null);
    } else {
      setOpenNotification(event.currentTarget);
    }
  };

  const handleCloseNotification = () => {
    setOpenNotification(null);
  };
  const handleClickProfile = (event) => {
    if (openProfile && openProfile.contains(event.target)) {
      setOpenProfile(null);
    } else {
      setOpenProfile(event.currentTarget);
    }
  };

  function changeLanguage(params) {
    window.localStorage.setItem("Language", params);
    i18n.changeLanguage(params);
    handleCloseLanguage();
    location.reload();
  }

  const handleClickLanguage = (event) => {
    if (openLanguage && openLanguage.contains(event.target)) {
      setOpenLanguage(null);
    } else {
      setOpenLanguage(event.currentTarget);
    }
  };
  const handleCloseLanguage = () => {
    setOpenLanguage(null);
  };

  const logoutFn = async () => {
    window.localStorage.removeItem("token");
    window.localStorage.removeItem("user");
    // Tear down the shared realtime socket so a same-tab re-login rebuilds it with the NEW user's
    // token. The disconnect handler intentionally keeps the singleton alive for transient
    // reconnects, so without this an in-tab account switch would reuse the prior user's connection.
    resetSocket();
    // An explicit sign-out must not leave a stashed deep-link destination behind
    // to hijack the next sign-in in this tab.
    clearRedirect();
    dispatch(resetMetaData({}));
    logout();
    props.history.push("/auth/login");
  };

  const chunk = (arr, size) =>
    arr.reduce(
      (acc, e, i) => (
        i % size ? acc[acc.length - 1].push(e) : acc.push([e]), acc
      ),
      []
    );

  function generateIcon(Icon, elm) {
    // console.log({ elm })
    // Guard: an app whose name has no `paths` entry used to crash the whole
    // drawer (<Icon/> with Icon === undefined). Render the label alone instead.
    return (
      <span>
        {Icon ? <Icon fontSize="medium" /> : null}
        <p className={classes.title}>{elm == 'Solution Management' ? 'Manage Solutions' : t(elm)}</p>
      </span>
    );
  }

  const paths = {
    Support: { path: "/support", icon: SupportAgentIcon },
    Solutions: { path: "/solutions", icon: WebIcon },
    Administration: { path: "/administration", icon: SupervisorAccountIcon },
    Notifications: { path: "/notifications", icon: NotificationsActiveIcon },
    "Solution Management": { path: "/solutionManagement", icon: CodeIcon },
    Analytics: {
      icon: AnalyticsIcon,
      path: "/analytics",
    },
    Alarms: {
      icon: NotificationsActiveIcon,
      path: "/alarms-dashboard",
    },
    "Fleet Reports": {
      icon: LocalShippingIcon,
      path: "/fleetReports",
    },
  };

  return (
    <div>
      <div style={{ display: "flex", gap: "10px" }}>
        <Search />
        <Popover
          icon={Person}
          component={popperProfile}
          white
          profile
          name="profile"
        />
        <Popover
          icon={LanguageIcon}
          component={popperLanguage}
          name="language"
          white
        />
        <Popover icon={AppsIcon} component={popperGrid} name="drawer" white />
      </div>
    </div>
  );
}
